import { createSlice } from "@reduxjs/toolkit";
import { Question, DetectionBox } from "src/data/types";

/** Helper function to create initial streaming metrics */
const createInitialStreamingMetrics = () => ({
  imagesSent: 0,
  currentImageSize: 0,
  currentLatency: 0,
  latencies: [],
  imageSizes: [],
  sessionStartTime: null,
  averageLatency: 0,
  averageImageSize: 0,
  p50Latency: 0,
  p90Latency: 0,
});

/** Helper function to reset streaming-related state fields */
const resetStreamingFields = (state: InspectionCaptureState) => {
  state.streamingMode = false;
  state.streamingDevMode = false;
  state.streamingConnected = false;
  state.streamingError = null;
  state.detectionBoxes = [];
  state.streamingGuidanceMessage = "";
  state.imageNextStage = null;
  state.streamingMetrics = createInitialStreamingMetrics();
};

export type InspectionCaptureState = {
  // Active question tracking
  activeQuestion: Question | null;
  activeObjectQuestions: Question[]; // For object-based multi-question mode
  
  // Camera selection
  selectedCameraDeviceId: string | null;
  
  // Standard capture mode
  capturedImageSrc: string;
  imageCapturingMode: boolean;
  cameraOpen: boolean;
  
  // Workflow states
  doneInspectingCurrentQuestion: boolean;
  readyToSubmitReport: boolean;
  
  // Barcode scanning
  barcodeScanningMode: boolean;
  scannedBarcode: string | null;
  scannedBarcodes: string[]; // Track all scanned barcodes in this session
  // Streaming mode states
  streamingMode: boolean;
  streamingDevMode: boolean; // Flag to enable dev controls and connection status
  streamingConnected: boolean;
  streamingError: string | null;
  detectionBoxes: DetectionBox[];
  streamingGuidanceMessage: string;
  imageNextStage: string | null; // "LOW_RES" | "HIGH_RES" | "READY_FOR_ANALYZER"
  streamingConfig: {
    quality: number;
    maxWidth: number;
    maxHeight: number;
    interval: number;
    resolutionScale: number; // 0.3, 0.5, 0.8, 1.0 for 30%, 50%, 80%, 100%
  };
  
  // Screen dimensions for responsive resolution calculation
  screenWidth: number;
  screenHeight: number;
  
  // Streaming metrics for dev mode
  streamingMetrics: {
    imagesSent: number;
    currentImageSize: number; // in KB
    currentLatency: number; // in ms
    latencies: number[]; // array for calculating percentiles
    imageSizes: number[]; // array for calculating average size
    sessionStartTime: number | null;
    averageLatency: number; // calculated average
    averageImageSize: number; // calculated average
    p50Latency: number; // median latency
    p90Latency: number; // 90th percentile latency
  };
};

const initialState: InspectionCaptureState = {
  activeQuestion: null,
  activeObjectQuestions: [],
  selectedCameraDeviceId: null,
  capturedImageSrc: "",
  imageCapturingMode: true,
  cameraOpen: false,
  doneInspectingCurrentQuestion: false,
  readyToSubmitReport: false,
  
  // Initialize barcode scanning states
  barcodeScanningMode: false,
  scannedBarcode: null,
  scannedBarcodes: [], // Initialize empty array
  // Initialize streaming mode states
  streamingMode: false,
  streamingDevMode: false,
  streamingConnected: false,
  streamingError: null,
  detectionBoxes: [],
  streamingGuidanceMessage: "",
  imageNextStage: null,
  streamingConfig: {
    quality: 0.6,
    maxWidth: 640,
    maxHeight: 480,
    interval: 800,
    resolutionScale: 0.5, // Default to 50% of screen resolution
  },
  
  // Initialize screen dimensions (will be updated on mount)
  screenWidth: 0,
  screenHeight: 0,
  
  // Initialize streaming metrics
  streamingMetrics: {
    imagesSent: 0,
    currentImageSize: 0,
    currentLatency: 0,
    latencies: [],
    imageSizes: [],
    sessionStartTime: null,
    averageLatency: 0,
    averageImageSize: 0,
    p50Latency: 0,
    p90Latency: 0,
  },
};

/** Inspection Capture Slice */
const { reducer, actions } = createSlice({
  name: "inspectionCapture",
  initialState,
  reducers: {
    setActiveQuestion: (state, { payload }) => {
      state.activeQuestion = payload;
    },
    setActiveObjectQuestions: (state, { payload }) => {
      state.activeObjectQuestions = payload;
    },
    setSelectedCameraDeviceId: (state, { payload }) => {
      state.selectedCameraDeviceId = payload;
    },
    setCapturedImageSrc: (state, { payload }) => {
      state.capturedImageSrc = payload;
    },
    setImageCapturingMode: (state, { payload }) => {
      state.imageCapturingMode = payload;
      // When enabling image capturing mode, disable streaming mode
      if (payload === true) {
        state.streamingMode = false;
      }
    },
    setCameraOpen: (state, { payload }) => {
      state.cameraOpen = payload;
    },
    attachImageToQuestion: (state, { payload }) => {
      if (state.activeQuestion) {
        state.activeQuestion.examples = payload;
      }
    },
    setDoneInspectingCurrentQuestion: (state, { payload }) => {
      state.doneInspectingCurrentQuestion = payload;
    },
    setReadyToSubmitReport: (state, { payload }) => {
      state.readyToSubmitReport = payload;
    },
    
    // Barcode scanning actions
    setBarcodeScanningMode: (state, { payload }) => {
      state.barcodeScanningMode = payload;
    },
    setScannedBarcode: (state, { payload }) => {
      state.scannedBarcode = payload;
    },
    clearBarcodeData: (state) => {
      state.scannedBarcode = null;
      state.barcodeScanningMode = false;
    },
    addScannedBarcode: (state, { payload }) => {
      // Add barcode to the list if not already present
      if (!state.scannedBarcodes.includes(payload)) {
        state.scannedBarcodes.push(payload);
      }
    },
    // Streaming mode actions
    setStreamingMode: (state, { payload }) => {
      state.streamingMode = payload;
      // When enabling streaming mode, disable regular image capturing mode
      if (payload === true) {
        state.imageCapturingMode = false;
      }
    },
    setStreamingDevMode: (state, { payload }) => {
      state.streamingDevMode = payload;
    },
    setStreamingConnected: (state, { payload }) => {
      state.streamingConnected = payload;
    },
    setStreamingError: (state, { payload }) => {
      state.streamingError = payload;
    },
    updateDetectionBoxes: (state, { payload }) => {
      // Handle both empty payload and null cases
      if (!payload || payload.length === 0) {
        state.detectionBoxes = [];
        return;
      }
      
      // Transform backend bounding boxes to frontend format if needed
      const processedBoxes = payload.map((box: {
        x: number;
        y: number; 
        width: number;
        height: number;
        confidence: number;
        label?: string;
        class?: string;
        class_id?: number;
      }) => {
        // Ensure box has a label property (frontend expects this)
        // Backend may use 'class' instead of 'label'
        if (!box.label && box.class) {
          return {
            ...box,
            label: box.class // Copy class to label for frontend compatibility
          };
        }
        return box;
      });
      
      state.detectionBoxes = processedBoxes;
    },
    setStreamingGuidanceMessage: (state, { payload }) => {
      state.streamingGuidanceMessage = payload;
    },
    setImageNextStage: (state, { payload }) => {
      state.imageNextStage = payload;
    },
    resetStreamingState: (state) => {
      resetStreamingFields(state);
    },
    setStreamingConfig: (state, { payload }: {
      payload: {
        quality?: number;
        maxWidth?: number;
        maxHeight?: number;
        interval?: number;
        resolutionScale?: number;
      }
    }) => {
      state.streamingConfig = { ...state.streamingConfig, ...payload };
    },
    setScreenDimensions: (state, { payload }: {
      payload: {
        width: number;
        height: number;
      }
    }) => {
      state.screenWidth = payload.width;
      state.screenHeight = payload.height;
    },
    
    // Streaming metrics actions
    updateStreamingMetrics: (state, { payload }: { 
      payload: { 
        imageSize?: number; 
        latency?: number;
      }
    }) => {
      if (payload.imageSize !== undefined) {
        state.streamingMetrics.currentImageSize = payload.imageSize;
        state.streamingMetrics.imagesSent += 1;
        state.streamingMetrics.imageSizes.push(payload.imageSize);
        
        // Keep only last 50 image sizes for average calculation
        if (state.streamingMetrics.imageSizes.length > 50) {
          state.streamingMetrics.imageSizes.shift();
        }
        
        // Calculate average image size
        const sizeSum = state.streamingMetrics.imageSizes.reduce((a, b) => a + b, 0);
        state.streamingMetrics.averageImageSize = Math.round(sizeSum / state.streamingMetrics.imageSizes.length);
      }
      if (payload.latency !== undefined) {
        state.streamingMetrics.currentLatency = payload.latency;
        state.streamingMetrics.latencies.push(payload.latency);
        
        // Keep only last 50 latencies for percentile calculation
        if (state.streamingMetrics.latencies.length > 50) {
          state.streamingMetrics.latencies.shift();
        }
        
        // Calculate average
        const sum = state.streamingMetrics.latencies.reduce((a, b) => a + b, 0);
        state.streamingMetrics.averageLatency = Math.round(sum / state.streamingMetrics.latencies.length);
        
        // Calculate percentiles
        if (state.streamingMetrics.latencies.length > 0) {
          const sorted = [...state.streamingMetrics.latencies].sort((a, b) => a - b);
          
          // P50 (median)
          const p50Index = Math.floor(sorted.length * 0.5);
          state.streamingMetrics.p50Latency = Math.round(sorted[p50Index]);
          
          // P90
          const p90Index = Math.floor(sorted.length * 0.9);
          state.streamingMetrics.p90Latency = Math.round(sorted[p90Index]);
        }
      }
    },
    initStreamingSession: (state) => {
      state.streamingMetrics.sessionStartTime = Date.now();
      state.streamingMetrics.imagesSent = 0;
      state.streamingMetrics.latencies = [];
      state.streamingMetrics.imageSizes = [];
      state.streamingMetrics.averageLatency = 0;
      state.streamingMetrics.averageImageSize = 0;
      state.streamingMetrics.p50Latency = 0;
      state.streamingMetrics.p90Latency = 0;
    },
    resetInspectionCaptureState: (state) => {
      state.activeQuestion = null;
      state.activeObjectQuestions = [];
      state.selectedCameraDeviceId = null;
      state.capturedImageSrc = "";
      state.imageCapturingMode = true;
      state.cameraOpen = false;
      state.doneInspectingCurrentQuestion = false;
      state.readyToSubmitReport = false;
      state.barcodeScanningMode = false;
      state.scannedBarcode = null;
      state.scannedBarcodes = []; // Reset scanned barcodes list
      resetStreamingFields(state);
    },
  },
});

export const {
  setActiveQuestion,
  setActiveObjectQuestions,
  setSelectedCameraDeviceId,
  setCapturedImageSrc,
  setImageCapturingMode,
  setCameraOpen,
  attachImageToQuestion,
  setDoneInspectingCurrentQuestion,
  setReadyToSubmitReport,
  setBarcodeScanningMode,
  setScannedBarcode,
  clearBarcodeData,
  addScannedBarcode,
  setStreamingMode,
  setStreamingDevMode,
  setStreamingConnected,
  setStreamingError,
  updateDetectionBoxes,
  setStreamingGuidanceMessage,
  setImageNextStage,
  resetStreamingState,
  setStreamingConfig,
  setScreenDimensions,
  updateStreamingMetrics,
  initStreamingSession,
  resetInspectionCaptureState,
} = actions;

export default reducer;
